/**
 * Provider group for the console pillars (Plan, Connect, the /docs standards,
 * Validate, Learn, Settings and the full-page editors under them).
 *
 * TenantShell gives every page its chrome and the light, always-on providers
 * (Tenant, Project, notifications). This wrapper adds only what the console
 * pages additionally need — WorkspaceProvider and PublishJobProvider, which
 * `useWorkspace`/`usePublishJob` throw without — so those workspace fetches stay
 * scoped to the console and never fire on the hub / Add / Gaps. It also supplies
 * the console's own scroll column (TenantShell's <main> is bounded) and the
 * publish-to-CI toast that PublishJobProvider drives.
 *
 * TenantNeutralGate is kept here (it travelled with the console routes before
 * the shell merge); on the tenant root host it is a passthrough, so it only
 * matters if these routes are ever reached on a neutral host.
 */

import { Outlet } from 'react-router-dom';
import {
  GitPullRequest,
  ExternalLink,
  X,
  AlertCircle,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { PublishJobProvider, usePublishJob } from '@/contexts/PublishJobContext';
import { TenantNeutralGate } from '@/components/layout/TenantNeutralGate';

function PublishJobToast() {
  const { publishing, result, error, dismiss } = usePublishJob();

  if (publishing) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/90 px-4 py-3 shadow-lg text-sm text-blue-700 dark:text-blue-300">
        <Spinner size="sm" tone="current" />
        Creating PR...
      </div>
    );
  }

  if (result) {
    return (
      <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/90 px-4 py-3 shadow-lg text-sm max-w-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-medium">
            <GitPullRequest className="w-4 h-4" />
            PR created: {result.scriptsCreated} script(s) published
          </div>
          {/* Matches the message beside it (emerald-700/300): emerald-400 was
              too pale against emerald-50 to make out in light mode (1.8:1). */}
          <button
            onClick={dismiss}
            className="text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <a
          href={result.prUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View Pull Request #{result.prNumber}
        </a>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/90 px-4 py-3 shadow-lg text-sm max-w-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4" />
            PR creation failed: {error}
          </div>
          <button onClick={dismiss} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export function ConsolePillars() {
  return (
    <TenantNeutralGate>
      <WorkspaceProvider>
        <PublishJobProvider>
          {/* The console's own scroll column — TenantShell's <main> is bounded. */}
          <div className="flex-1 overflow-y-auto">
            <Outlet />
          </div>
          <PublishJobToast />
        </PublishJobProvider>
      </WorkspaceProvider>
    </TenantNeutralGate>
  );
}
